import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, Row, Col, InputGroup, InputGroupText, Input, Button, Spinner } from "reactstrap";
import { BsBarChartFill, BsSearch } from "react-icons/bs";
import PageHeader from "../../sharedComponents/Header/PageHeader";
import MobileMenuSlideDrawer from "../../sharedComponents/SlideDrawer/MobileMenuSlideDrawer";
import { filtersDateRanges, Theme } from "../../config/constants";
import { IoMdInformationCircle } from "react-icons/io";
import { getLast6Months } from "../../utils/dateUtils";
import datasuiteApi from "../../api/datasuiteApi";
import { toast } from "react-toastify";
import EmptyDataMessage from "../../sharedComponents/ErrorMessages/EmptyDataMessage";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { downloadCsvFile } from "../../config/dataManipulations";
import useManufacturerData from "../../sharedComponents/useManufacturerData/useManufacturerData";
import ManufacturerSelect from "../../sharedComponents/ManufacturerSelect/ManufacturerSelect";
import FiltersWidget from "../../sharedComponents/Filters/FiltersWidget";
import { useQuery } from "react-query";
import { showModal } from "../../reducers/ModalsReducer/ModalsActions";
import ShareOpportunityModal from "./Modals/ShareOpportunityModal";
import { isMobileOnly } from "react-device-detect";
import { FaRegShareSquare, FaDownload } from "react-icons/fa";
import CollapsibleDataTable from "../../sharedComponents/Tables/CollapsibleDataTable";

const MatchOpportunityIdentifier = () => {
    const dispatch = useDispatch();
    const chainData = useSelector(({user}) => user.chainData);
    const emergingData = useSelector(({user}) => user.emergingData);
    const infoString = "Our Match Opportunity Identifier algorithm compares your products to competitors using Category, Name, and Pack Size. If no match is found, you’ll see it as an Opportunity in “Growth Opportunities” but not the item it matches.";
    const tableString = "These are matched items for the selected category.";
    const [startDate, endDate] = getLast6Months();
    const [inputText, setInputText] = useState('');
    const [prechosenFilters, setPrechosenFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState(null);
    const [itemMatchData, setItemMatchData] = useState(null);
    const [dataLoading, setDataLoading] = useState(true);
    const { manufacturerParams } = useManufacturerData();

    const getTableColumns = (selectedFilters) => [
        {
            label: "Parent Group",
            dataKey: "location_name",
            width: "0.225",
            formatter: "none",
            headerRenderer: 'true'
        }, 
        {
            label: "Description",
            dataKey: "match_item_description",
            width: "0.2",
            formatter: "none",
            headerRenderer: 'true'
        },
        {
            label: "Category",
            dataKey: "category",
            width: "0.175",
            formatter: "none",
            headerRenderer: 'true'
        },
        {
            label: "Pack Size",
            dataKey: "match_item_pack",
            width: "0.1",
            formatter: "none",
            disableSort: 'true'
        },
        {
            label: "Your Cases",
            dataKey: "your_cases",
            width: "0.125",
            formatter: "number",
            headerRenderer: 'true'
        },
        {
            label: "Opportunity Cases",
            dataKey: "opportunity_cases",
            width: "0.2",
            formatter: "number",
            headerRenderer: 'true'
        },
        {
            label: "Total Cases",
            dataKey: "total_cases",
            width: "0.125",
            formatter: "number",
            headerRenderer: 'true'
        }
    ];

    const useGetCategoryFilters = () => useQuery({
        queryKey: ['categoryFilters'],
        queryFn: () => datasuiteApi.filters.post({
            "chainData": chainData,
            "emergingData": emergingData,
            "startDate": startDate,
            "endDate": endDate,
            ...manufacturerParams
        }).then((response) => {
            return response.data.categories;
        }).catch(err => {
            console.log(err);
        }),
        enabled: chainData || emergingData
    });
    const { refetch: refetchCategoryFilters } = useGetCategoryFilters();

    const getMatchOpportunityItems = (selectedFilters = []) => {
        const params = {
            startDate: selectedFilters?.startDate,
            endDate: selectedFilters?.endDate,
            chainData: selectedFilters?.chainData,
            emergingData: selectedFilters?.emergingData,
            categories: selectedFilters?.categories,
            distributors: selectedFilters?.distributors,
            locations: selectedFilters?.locations,
            segments: selectedFilters?.segments,
            countries: selectedFilters?.countries,
            states: selectedFilters?.states,
            parentSizeMin: selectedFilters?.parentSizeMin,
            parentSizeMax: selectedFilters?.parentSizeMax,
            ...manufacturerParams
        };
        datasuiteApi.matchOpportunityIdentifier.post(params).then((response) => {
            setItemMatchData(response.data);
            setDataLoading(false);
        }).catch((err) => {
            console.log(err);
            toast.error(err.message);
            setDataLoading(false);
        });
    };

    const onClickSearch = (selectedFilters = []) => {
        setDataLoading(true);
        setSelectedFilters(selectedFilters);
        getMatchOpportunityItems(selectedFilters);
    };

    const onClickExport = () => {
        const filename = "BEP_Match_Opportunities_" + moment().format('YYYYMMDD_hhmmss').toString();
        const exportData = (itemMatchData ?? []).flatMap(({ child_locations, match_item_min, match_item_id, location_platform_id, ...parentRowData }) => {
            if (!Array.isArray(child_locations) || child_locations.length === 0) {
                return [{ ...parentRowData }];
            }
            return child_locations.map(({ child_location_platform_id, ...childLocation }) => ({
                ...parentRowData,
                ...childLocation
            }));
        });
        downloadCsvFile(exportData, filename);
        toast.success('Data successfully exported to downloads folder.')
    };

    const onClickShareReport = () => {
        dispatch(showModal(ShareOpportunityModal, {selectedFilters: selectedFilters ?? prechosenFilters}));
    };

    const filteredItemMatchData = (() => {
        const sourceRows = itemMatchData ?? [];
        const searchTerm = inputText.trim().toLowerCase();
        if (!searchTerm) {
            return sourceRows;
        }
        const includesSearchTerm = (value) => String(value ?? '').toLowerCase().includes(searchTerm);
        return sourceRows.filter((row) => {
            const parentMatches = [
                row?.location_name,
                row?.match_item_description,
                row?.category,
                row?.match_item_pack
            ].some(includesSearchTerm);
            if (parentMatches) {
                return true;
            }
            return (row?.child_locations ?? []).some((childLocation) => {
                return [
                    childLocation?.child_location_name,
                    childLocation?.child_location_city,
                    childLocation?.child_location_state
                ].some(includesSearchTerm);
            });
        });
    })();

    useEffect(() => {
        const fetchCategories = async () => {
            const categoryFilters = await refetchCategoryFilters().then((res) => {
                return res.data;
            }).catch(err => { return []; });
            if (categoryFilters?.length > 0) {
                categoryFilters.sort();
                setPrechosenFilters({
                    categories: [categoryFilters[0]]
                });
                getMatchOpportunityItems({
                    startDate,
                    endDate,
                    chainData,
                    emergingData,
                    categories: [categoryFilters[0]]
                });
            } else {
                setDataLoading(false);
            }
        };
        fetchCategories();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <PageHeader title='SalesStream' subtitle='Match Opportunity Identifier' icon={<BsBarChartFill fill='url(#icon-gradient)'/>}/>
            <div className='bg-transparent p-3 matchOpportunityIdentifierContainer'>
                <Row className='label-text text-size-14 d-flex align-items-center justify-content-end mx-auto pb-2'>
                    <Col className="pe-0">
                        <ManufacturerSelect />
                    </Col>
                </Row>
                <Card style={{minHeight: '56px', background: Theme().colors.accent}}>
                    <Col className="w-100">
                        <Row className="w-100 h-100 m-auto" style={{alignItems: 'center'}}>
                            <Col className="w-fc">
                                <IoMdInformationCircle style={{fontSize: '20px', color: 'white'}} />
                            </Col>
                            <Col style={{fontWeight: '600', color: 'white'}}>
                                {infoString}
                            </Col>
                        </Row>
                    </Col>
                </Card>
                <FiltersWidget
                    minDate={startDate}
                    maxDate={endDate}
                    dateRanges={filtersDateRanges}
                    fromDate={startDate}
                    toDate={endDate}
                    prechosenFilters={selectedFilters ?? prechosenFilters}
                    saveFilterSetsEnabled={true}
                    filterSetOptionsEnabled={true}
                    onApplyFilters={onClickSearch}
                />
                <Card className="px-2 py-3" style={{height: 'calc(100vh - 140px)'}}>
                    <Col style={{display: 'flex', flexDirection: 'column'}}>
                        <Row className="w-100 mx-auto pe-0" style={{paddingBottom: '12px'}}>
                            <Col className="main-header text-size-18" style={{alignContent: 'center'}}>
                                <span>Match Opportunities</span>
                            </Col>
                            {!isMobileOnly &&
                            <>
                            <Col className="w-fc ps-0" style={{alignContent: 'center'}}>
                                <Tooltip title={tableString} placement="top" arrow>
                                    <div style={{position: 'relative', bottom: '2px'}}>
                                        <IoMdInformationCircle color={Theme().colors.accent} style={{fontSize: "18px"}} />
                                    </div>
                                </Tooltip>
                            </Col>
                            <Col></Col>
                            <Col className="w-fc">
                                <Button
                                    outline
                                    className='py-1 button-label'
                                    size='sm'
                                    style={{width: '80px'}}
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={() => onClickShareReport()}>Share</Button>
                            </Col>
                            <Col className="w-fc">
                                <Button
                                    outline
                                    className='py-1 button-label'
                                    size='sm'
                                    style={{width: '80px'}}
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={onClickExport}>Export</Button>
                            </Col>
                            </>
                            }
                            {isMobileOnly &&
                            <>
                            <Col className="w-fc px-0 pe-2" style={{alignContent: 'center'}}>
                                <Button
                                    block
                                    className='py-1 button-label'
                                    size='sm'
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={() => onClickShareReport()}><FaRegShareSquare /></Button>
                            </Col>
                            <Col className="w-fc px-0 pe-2" style={{alignContent: 'center'}}>
                                <Button
                                    block
                                    className='py-1 button-label'
                                    size='sm'
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={onClickExport}><FaDownload /></Button>
                            </Col>
                            </>
                            }
                        </Row>
                        <Row>
                            <Col>
                                <InputGroup>
                                    <InputGroupText>
                                        <BsSearch/>
                                    </InputGroupText>
                                    <Input className='text-input' value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder='Search...'/>
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row className="mx-auto" style={{marginTop: '8px', flex: '1', width: '100%', overflowX: 'overlay'}}>
                            {(filteredItemMatchData?.length <= 0 || filteredItemMatchData == null) && !dataLoading &&
                            <Col className="m-auto">
                                <EmptyDataMessage />
                                <div style={{textAlign: 'center'}}>Try broadening your search (at least one Category is required).</div>
                            </Col>
                            }
                            {dataLoading &&
                            <Col className="m-auto">
                                <div className="mx-auto my-auto" style={{height:"2.5%", width:"2.5%"}}>
                                    <Spinner animation="border" />
                                </div>
                            </Col>
                            }
                            {filteredItemMatchData?.length > 0 && !dataLoading &&
                            <Col className='px-0' style={{height: '100%', minWidth: '1000px'}}>
                                <CollapsibleDataTable list={filteredItemMatchData} tableColumns={getTableColumns(selectedFilters)} tableName={"matchOpportunitiesTable"} selectedFilters={selectedFilters} widgetTitle={"Match Opportunities"} />
                            </Col>
                            }
                        </Row>
                    </Col>
                </Card>
            </div>
            <MobileMenuSlideDrawer />
        </>

    )
}

export default MatchOpportunityIdentifier;
